import { ArrowRight, FileText, Globe, TestTube2 } from "lucide-react";

import { cn } from "products/muggleTestV0/lib/utils";
import type { HeroWorkflowFlow } from "types/landing/landing.types";

type HeroWorkflowFlowPreviewProps = {
  content: HeroWorkflowFlow;
};

/**
 * Simple 3-step flow: Input → Muggle → Output (tangible result).
 */
export function HeroWorkflowFlowPreview({
  content: _content,
}: HeroWorkflowFlowPreviewProps) {
  return (
    <div className="w-full rounded-2xl bg-gradient-to-br from-accent/[0.04] via-background to-muted/30 border border-accent/10 px-6 py-6 shadow-lg shadow-accent/[0.03] ring-1 ring-white/5 sm:px-8 sm:py-8 backdrop-blur-sm">
      <p className="mb-8 text-center text-lg font-bold text-foreground">
        End-to-end testing in <span className="text-accent">3 simple steps</span>
      </p>

      <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-3 lg:gap-4">
        <WorkflowStep
          icon={Globe}
          label="Paste URL"
          sublabel="Localhost or preview"
          isHighlighted={false}
          step="1"
        />

        <FlowArrow />

        <WorkflowStep
          icon={TestTube2}
          label="Muggle Tests"
          sublabel="AI validates journeys"
          isHighlighted={true}
          step="2"
        />

        <FlowArrow />

        <WorkflowStep
          icon={FileText}
          label="Get Report"
          sublabel="Bugs + evidence"
          isHighlighted={false}
          step="3"
        />
      </div>

      <p className="mt-8 text-center text-sm text-muted-foreground">
        Works with any preview host — localhost, Vercel, Netlify, etc.
      </p>
    </div>
  );
}

type WorkflowStepProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  sublabel: string;
  isHighlighted: boolean;
  step: string;
};

function WorkflowStep({ icon: Icon, label, sublabel, isHighlighted, step }: WorkflowStepProps) {
  return (
    <div className="flex w-full flex-col items-center sm:w-0 sm:flex-1 sm:min-w-0">
      <div className="relative mb-3">
        <div
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-full",
            isHighlighted
              ? "bg-accent text-accent-foreground shadow-lg shadow-accent/20"
              : "bg-muted text-muted-foreground"
          )}
        >
          <Icon className="h-6 w-6" />
        </div>
        <span className={cn(
          "absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold",
          isHighlighted
            ? "bg-accent-foreground text-accent"
            : "bg-foreground/10 text-muted-foreground"
        )}>
          {step}
        </span>
      </div>

      <span
        className={cn(
          "text-center text-base font-semibold",
          isHighlighted ? "text-accent" : "text-foreground"
        )}
      >
        {label}
      </span>

      <span className="mt-1 text-center text-sm text-muted-foreground">
        {sublabel}
      </span>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex items-center justify-center px-1 py-1 sm:px-1 sm:py-0">
      <ArrowRight className="h-5 w-5 rotate-90 text-muted-foreground/30 sm:rotate-0" />
    </div>
  );
}
